<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { RouterView } from 'vue-router'
import Toast from 'primevue/toast'
import ProgressSpinner from 'primevue/progressspinner'
import { useLoadingStore } from '@/stores/loading'

const loadingStore = useLoadingStore()

// Masquer le loader HTML natif quand l'app est prête
watch(() => loadingStore.appReady, (ready) => {
  if (ready) {
    const loader = document.querySelector('.loading-page')
    if (loader) {
      loader.classList.add('fade-out')
      setTimeout(() => {
        loader.remove()
      }, 300)
    }
  }
}, { immediate: true })

// Microsoft Clarity Analytics (production uniquement)
onMounted(() => {
  const mode = import.meta.env.VUE_APP_MODE || import.meta.env.MODE
  if (mode === 'prod' || mode === 'production') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (function (c: any, l: any, a: any, r: any, i: any) {
      c[a] = c[a] || function (...args: unknown[]) { (c[a].q = c[a].q || []).push(args) }
      const t = l.createElement(r)
      t.async = true
      t.src = 'https://www.clarity.ms/tag/' + i
      const y = l.getElementsByTagName(r)[0]
      if (y && y.parentNode) {
        y.parentNode.insertBefore(t, y)
      }
    })(window, document, 'clarity', 'script', '8ue791yd2m')
  }
})
</script>

<template>
  <div class="arago-app">
    <!-- Contenu de l'application (masqué pendant le chargement) -->
    <template v-if="loadingStore.appReady">
      <RouterView v-slot="{ Component }">
        <Transition
          name="fade"
          mode="out-in"
        >
          <component :is="Component" />
        </Transition>
      </RouterView>
    </template>

    <template v-else>
      <div v-if="loadingStore.appError" class="loading-overlay">
        <div class="loading-error-title">Initialisation interrompue</div>
        <div class="loading-message">{{ loadingStore.appError }}</div>
      </div>

      <!-- Spinner pendant le chargement initial (remplace la page blanche) -->
      <div v-else class="loading-overlay">
        <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" animationDuration=".5s" />
        <div class="loading-message">{{ loadingStore.loadingMessage || "Chargement de l'application..." }}</div>
      </div>
    </template>
    
    <!-- Global Loading Overlay (utilisé pendant les opérations APRÈS le chargement initial) -->
    <div v-if="loadingStore.appReady && loadingStore.isLoading" class="loading-overlay">
      <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" animationDuration=".5s" />
      <div v-if="loadingStore.loadingMessage" class="loading-message">{{ loadingStore.loadingMessage }}</div>
    </div>
    
    <!-- PrimeVue Toast pour les notifications -->
    <Toast />
  </div>
</template>

<style lang="scss">
// Reset et styles globaux
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.arago-app {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
  min-height: 100vh;
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  .app-dark & {
    background-color: rgba(17, 24, 39, 0.85);
  }
}

.loading-message {
  margin-top: 1rem;
  font-weight: 600;
  color: var(--arago-primary);
}

.loading-error-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--arago-primary);
}
</style>
